import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X } from "lucide-react";
import Seo from "@/components/Seo";
import PageIntro from "@/components/PageIntro";
import { ApparitionCard } from "@/components/landing/Apparitions";
import { CHRONOLOGICAL, STATUS_LABELS } from "@/data/apparitions";
import { EASE } from "@/components/landing/Reveal";

const CENTURIES = [...new Set(CHRONOLOGICAL.map((entry) => entry.century))].sort((a, b) => a - b);
const ordinal = (c) => `${c}th century`;

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-pressed={active}
    className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-all duration-300 ${
      active ? "border-gold bg-midnight text-ivory" : "border-marian/25 bg-white/70 text-marian hover:border-gold"
    }`}
  >
    {children}
  </button>
);

export default function ApparitionsIndex() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [century, setCentury] = useState(null);
  const [label, setLabel] = useState(null);
  const country = params.get("country");

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CHRONOLOGICAL.filter(
      (a) =>
        (!century || a.century === century) &&
        (!label || a.labels.includes(label)) &&
        (!country || a.country === country) &&
        (!q || [a.title, a.country, a.place, a.visionary, a.feast, ...a.themes].join(" ").toLowerCase().includes(q))
    );
  }, [query, century, label, country]);

  const countries = new Set(CHRONOLOGICAL.map((a) => a.country)).size;

  return (
    <main data-testid="apparitions-index-page">
      <Seo
        title="Apparitions & Shrines · Theotokos · Mater Dei"
        siteName="Theotokos · Mater Dei"
        description={`${CHRONOLOGICAL.length} Marian apparition and devotional entries, from the Brown Scapular tradition to Kibeho, with exact Church status, full accounts, prayers, and sources.`}
      />
      <PageIntro
        crumbs={[{ label: "Home", to: "/" }, { label: "Apparitions & Shrines" }]}
        eyebrow="Our Lady around the world"
        lines={["Apparitions", "& Shrines"]}
        aside={
          <dl className="grid grid-cols-3 gap-4 rounded-2xl border border-marian/15 bg-white/70 p-5 text-center">
            {[
              [CHRONOLOGICAL.length, "entries"],
              [countries, "countries"],
              [CENTURIES.length, "centuries"],
            ].map(([n, l]) => (
              <div key={l} data-testid={`collection-count-${l}`}>
                <dt className="font-serif text-3xl font-semibold text-midnight">{n}</dt>
                <dd className="font-mono text-[10px] uppercase tracking-[0.2em] text-slateblue">{l}</dd>
              </div>
            ))}
          </dl>
        }
      >
        Places where Catholics have heard powerful calls to prayer, conversion, reconciliation,
        and hope. Every entry names its exact Church status — never a bare “approved” — and
        ends with a visible panel of sources.
      </PageIntro>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-8 lg:px-16">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <label className="relative block w-full lg:max-w-sm">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slateblue" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="apparitions-search"
              placeholder="Search by place, title, visionary, theme…"
              className="w-full rounded-full border border-marian/20 bg-white/80 py-3 pl-11 pr-4 text-sm text-midnight outline-none transition-colors placeholder:text-slateblue/70 focus:border-gold"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            {CENTURIES.map((c) => (
              <Chip key={c} testid={`filter-century-${c}`} active={century === c} onClick={() => setCentury(century === c ? null : c)}>
                {ordinal(c)}
              </Chip>
            ))}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slateblue">Church status</span>
          {Object.entries(STATUS_LABELS)
            .filter(([k]) => k !== "discernment" && k !== "nihil")
            .map(([k, v]) => (
              <Chip key={k} testid={`filter-status-${k}`} active={label === k} onClick={() => setLabel(label === k ? null : k)}>
                {v}
              </Chip>
            ))}
          {country && (
            <button
              type="button"
              data-testid="clear-country-filter"
              onClick={() => setParams({})}
              className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-3 py-1.5 text-xs font-semibold text-midnight"
            >
              {country} <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-slateblue" data-testid="results-count">
          {list.length} {list.length === 1 ? "entry" : "entries"} · chronological
        </p>

        <motion.div layout className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="apparitions-grid">
          <AnimatePresence mode="popLayout">
            {list.map((entry, i) => (
              <motion.div
                key={entry.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.45, ease: EASE, delay: Math.min(i * 0.04, 0.3) }}
              >
                <ApparitionCard entry={entry} headingLevel="h2" />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {list.length === 0 && (
          <p className="mt-10 rounded-2xl border border-dashed border-marian/25 p-10 text-center font-serif text-xl italic text-midnight" data-testid="no-results">
            No entry matches — try another place, century, or status.
          </p>
        )}
      </section>
    </main>
  );
}
